import { useState } from 'react';
import { siteConfig } from '@/mocks/siteConfig';

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('contact_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9qociee3oq4jqumtntg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'contact_alt')
            .map(([k, v]) => [k, String(v)])
        ).toString(),
      });

      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && (serverMsg.toLowerCase().includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormStatus('success');
          form.reset();
          return;
        }
        setFormStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
            联系我们
          </h2>
          <p className="text-sm text-secondary-500">
            如有任何问题或建议，欢迎通过以下方式联系我们
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h4>
                  <a href={`mailto:${siteConfig.email}`} className="text-sm text-secondary-600 hover:text-primary-500 transition-colors cursor-pointer">
                    {siteConfig.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 flex-shrink-0">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground-900 mb-1">工作时间</h4>
                  <p className="text-sm text-secondary-600">周一至周五 9:00 - 18:00</p>
                  <p className="text-xs text-secondary-500 mt-1">我们会在24小时内回复您的邮件</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-4"
            >
              {formStatus === 'success' && (
                <div className="bg-accent-100/80 text-accent-800 text-sm px-4 py-3 rounded-lg">
                  感谢您的留言！我们会在24小时内回复您。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="bg-primary-100 text-primary-800 text-sm px-4 py-3 rounded-lg">
                  {formError}
                </div>
              )}

              <div className="website-alt-field">
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="您的姓名"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-secondary-400 focus:outline-none focus:border-primary-300 transition-colors"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="您的邮箱"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-secondary-400 focus:outline-none focus:border-primary-300 transition-colors"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="subject"
                  placeholder="主题"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-secondary-400 focus:outline-none focus:border-primary-300 transition-colors"
                />
              </div>
              <div>
                <textarea
                  name="message"
                  placeholder="请输入您的留言内容（500字以内）"
                  rows={4}
                  maxLength={500}
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-secondary-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                ></textarea>
                <p className="text-xs text-secondary-400 mt-1 text-right">最多500字符</p>
              </div>
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </span>
                ) : (
                  '发送留言'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}